/**
 * The running tenant's geography as the logs path reads it (brief
 * 2026-10-08 §8.4.4, phase G2 "the logs path reads the model"): the
 * destinations a log name starts with, their order, labels and inks, their
 * sectors, and which entity (company) a log belongs to.
 *
 * Module state on purpose, like `tenantLocale.ts`: `logCountry(row)`, the
 * import's batch-name check and the flight check's options are plain
 * functions called outside React. `useOpsGeo()` re-renders a component when
 * the geography changes.
 *
 * It is loaded once a staff member is signed in (`companyBoot.ts`
 * `loadStaffCompany`, next to the company profile) and follows every
 * `useGeo()` answer (Admin ▸ Destinations & sectors refreshes it after a
 * save). Until then, and whenever the answer is missing or malformed, it is
 * {@link BUILTIN_GEO_SNAPSHOT}: Asian Trails' MY / SG / BN, the values every
 * page hard-coded before, which is also what the API serves before
 * `db/2026-10-15_geo_model.sql` (`GeoCatalog.Builtin`).
 */
import { useSyncExternalStore } from 'react';
import { inkCss, makeGeo, type Geo, type GeoDestination, type GeoSectorPublic, type GeoSnapshot } from './geo';

function dest(code: string, label: string, entity: string, zone: string, phone: string, ink: string, sector: string, order: number): GeoDestination {
  return {
    code, label, entity_code: entity, countries: [entity], time_zone: zone, phone_codes: [phone], ink,
    default_sector: sector, guide_slug: null, show_in_logs: true, show_in_filters: true, sort_order: order, is_active: true,
  };
}

function sector(code: string, name: string, destination: string, aliases: string[], airports: string[], crossBorderTo: string | null, ink: string | null, order: number): GeoSectorPublic {
  return { code, name, destination, aliases, airports, cross_border_to: crossBorderTo, ink, sort_order: order };
}

/** The API's `GeoCatalog.Builtin()`: Asian Trails' geography. */
export const BUILTIN_GEO_SNAPSHOT: GeoSnapshot = Object.freeze({
  source: 'builtin',
  entities: [
    { code: 'MY', name: 'Malaysia', ref_label: null, currency: null, sort_order: 1 },
    { code: 'SG', name: 'Singapore', ref_label: null, currency: null, sort_order: 2 },
  ],
  destinations: [
    dest('MY', 'Malaysia', 'MY', 'Asia/Kuala_Lumpur', '60', 'log-my', 'KUL', 1),
    dest('SG', 'Singapore', 'SG', 'Asia/Singapore', '65', 'log-sg', 'SG', 2),
    dest('BN', 'Borneo', 'MY', 'Asia/Kuching', '60', 'log-bn', 'BN', 3),
  ],
  sectors: [
    sector('KUL', 'Kuala Lumpur', 'MY', [], ['KUL', 'SZB'], null, 'log-my', 1),
    sector('PEN', 'Penang', 'MY', [], ['PEN'], null, null, 2),
    sector('LGK', 'Langkawi', 'MY', [], ['LGK'], null, null, 3),
    sector('MY-SG', 'Malaysia to Singapore', 'MY', [], [], 'SG', null, 4),
    sector('SG', 'Singapore', 'SG', ['SIN'], ['SIN'], null, 'log-sg', 5),
    sector('BN', 'Borneo', 'BN', ['BKI', 'KCH', 'SDK'], ['BKI', 'KCH', 'SDK'], null, 'log-bn', 6),
  ],
}) as GeoSnapshot;

export const BUILTIN_GEO: Geo = makeGeo(BUILTIN_GEO_SNAPSHOT);

let current: Geo = BUILTIN_GEO;
let currentKey = JSON.stringify(BUILTIN_GEO_SNAPSHOT);
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

function isSnapshot(raw: unknown): raw is GeoSnapshot {
  if (!raw || typeof raw !== 'object') return false;
  const r = raw as Record<string, unknown>;
  return Array.isArray(r.destinations) && Array.isArray(r.sectors)
    && (r.destinations as unknown[]).every((d) => !!d && typeof d === 'object' && typeof (d as { code?: unknown }).code === 'string');
}

/**
 * Apply `GET /api/geo`'s snapshot. A malformed answer, or one with no
 * active destination, keeps what runs (a tenant without destinations
 * cannot name a log). Returns the geography now running.
 */
export function applyGeoSnapshot(raw: unknown): Geo {
  if (!isSnapshot(raw)) return current;
  const next = makeGeo(raw);
  if (next.destinations.length === 0) return current;
  const key = JSON.stringify(raw);
  if (key !== currentKey) {
    current = next;
    currentKey = key;
    emit();
  }
  return current;
}

/** Back to Asian Trails' built-in geography (tests, sign-out). */
export function resetOpsGeo(): void {
  if (current !== BUILTIN_GEO) {
    current = BUILTIN_GEO;
    currentKey = JSON.stringify(BUILTIN_GEO_SNAPSHOT);
    emit();
  }
}

export function getOpsGeo(): Geo {
  return current;
}

export function subscribeOpsGeo(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** The running geography, re-rendering when it changes. */
export function useOpsGeo(): Geo {
  return useSyncExternalStore(subscribeOpsGeo, getOpsGeo, getOpsGeo);
}

/** Load it for a signed-in staff member (never throws; an older API keeps the built-in one). */
export async function loadOpsGeo(timeoutMs = 5_000): Promise<void> {
  try {
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = setTimeout(() => ctrl?.abort(), timeoutMs);
    try {
      const res = await fetch('/api/geo', { credentials: 'include', signal: ctrl?.signal });
      if (!res || !res.ok) return;
      const body = (await res.json()) as { data?: unknown } | null;
      if (body && typeof body === 'object' && 'data' in body) applyGeoSnapshot(body.data);
    } finally {
      clearTimeout(timer);
    }
  } catch {
    // Keep what runs.
  }
}

// ── Reading the logs path ──────────────────────────────────────────────

const norm = (v: string | null | undefined) => (v ?? '').trim().toUpperCase();

/** Active destination codes, in order (MY, SG, BN for Asian Trails). */
export function destinationCodes(geo: Geo = current): string[] {
  return geo.destinations.map((d) => norm(d.code));
}

/**
 * The destination codes with a page's historical order kept: the codes of
 * `legacy` the tenant has, in that order, then any other destination in its
 * own order. Asian Trails' pages that listed "MY, BN, SG" keep doing so;
 * a tenant without Borneo reads "MY, SG"; a new destination joins at the end.
 */
export function destinationCodesIn(legacy: readonly string[], geo: Geo = current): string[] {
  const codes = destinationCodes(geo);
  return [...legacy.filter((c) => codes.includes(c)), ...codes.filter((c) => !legacy.includes(c))];
}

/**
 * The sector codes with a page's historical order kept: the codes of
 * `legacy` the tenant has as sectors, then its other sectors in order,
 * cross-border ones (MY-SG) left out as the old lists did.
 */
export function sectorCodesIn(legacy: readonly string[], geo: Geo = current): string[] {
  const codes = geo.sectors.filter((s) => !s.cross_border_to).map((s) => norm(s.code));
  return [...legacy.filter((c) => codes.includes(c)), ...codes.filter((c) => !legacy.includes(c))];
}

/** The destinations the Logs page lists (show_in_logs not switched off), in order. */
export function logDestinations(geo: Geo = current): GeoDestination[] {
  return geo.destinations.filter((d) => d.show_in_logs !== false);
}

/** The first destination: an unrecognised log's (MY for Asian Trails). */
export function defaultDestination(geo: Geo = current): string {
  return destinationCodes(geo)[0] ?? 'MY';
}

/**
 * The destination a log name belongs to: the longest destination code it
 * starts with ("MY 14-20.3" is MY), case-insensitive; null when none. For
 * Asian Trails this is the old `startsWith('SG' | 'BN' | 'MY')`.
 */
export function destinationOfLog(log: string | null | undefined, geo: Geo = current): string | null {
  const l = norm(log);
  if (!l) return null;
  const codes = destinationCodes(geo).sort((a, b) => b.length - a.length);
  return codes.find((c) => l.startsWith(c)) ?? null;
}

/** "Malaysia" for MY; the code when the destination has no label or is unknown. */
export function destinationLabel(code: string | null | undefined, geo: Geo = current): string {
  const d = geo.destination(code);
  return d?.label?.trim() || norm(code);
}

/**
 * A destination's sectors that a Logs sub-sector pill can filter (in order).
 * Cross-border sectors count: MY-SG is a Malaysian pill today.
 */
export function destinationSectors(code: string, geo: Geo = current): GeoSectorPublic[] {
  return geo.sectorsOf(code);
}

/**
 * A line's sector to its destination: a destination code is itself, an exact
 * sector code is its destination; anything else null. Asian Trails: KUL,
 * PEN, LGK, MY-SG are MY; SG is SG; BN is BN.
 */
export function destinationOfSector(sectorCode: string | null | undefined, geo: Geo = current): string | null {
  const s = norm(sectorCode);
  if (!s) return null;
  if (destinationCodes(geo).includes(s)) return s;
  const hit = geo.sectors.find((x) => norm(x.code) === s);
  return hit?.destination ? norm(hit.destination) : null;
}

/**
 * The entity (company) a log's lines belong to: its destination's entity,
 * else today's mapping (a log starting "SG" is Singapore, the rest
 * Malaysia). Asian Trails: SG is SG; MY and BN are MY.
 */
export function entityOfLog(log: string | null | undefined, geo: Geo = current): string {
  const d = destinationOfLog(log, geo);
  const e = d ? norm(geo.destination(d)?.entity_code) : '';
  if (e) return e;
  return norm(log).startsWith('SG') ? 'SG' : 'MY';
}

/** The codes in words, in order: "MY, SG or BN". */
export function codesInWords(conjunction = 'or', geo: Geo = current): string {
  const c = destinationCodes(geo);
  if (c.length <= 1) return c[0] ?? '';
  return `${c.slice(0, -1).join(', ')} ${conjunction} ${c[c.length - 1]}`;
}

/** The CSS for a destination's ink (the Logs hues for MY / SG / BN). */
export function destinationInk(code: string | null | undefined, geo: Geo = current): string {
  const d = geo.destination(code);
  const key = d?.ink ?? null;
  if (key === 'log-my' || key === 'log-sg' || key === 'log-bn') return `var(--${key})`;
  if (key) return inkCss(key);
  return 'var(--text-muted)';
}
